import { Capacitor } from '@capacitor/core';
import { Badge, Box, Button, Stack } from '@mui/material';
import { InlineStatus } from 'components/InlineStatus';
import {
  MapEntityDetailsLayout,
  useMapEntityDetailsTabs,
} from 'features/mapping/components/MapEntityDetailsLayout';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { GeoEventEvacuationsButton } from 'features/geoEvents/components/GeoEventEvacuationsButton';
import { GeoEventNotificationsButton } from 'features/geoEvents/components/GeoEventNotificationsButton';
import { GeoEventUpdateTime } from 'features/geoEvents/components/GeoEventUpdateTime';
import { GeoEventCreatedByLine } from 'features/geoEvents/components/GeoEventMetadataRows';
import { geoEventShareButtonSx } from 'features/geoEvents/geoEvents.utils';
import { shareGeoEvent } from 'features/geoEvents/geoEvent.utils';
import {
  GeoEventEvacuationInfo,
  hasGeoEventEvacuationInfo,
} from 'features/evacZones/components/GeoEventEvacuationInfo';
import { GeoEventReports } from 'features/reports/components/GeoEventReports';
import { Locations } from 'features/locations/components/Locations';
import { Flooding } from 'features/flooding/flooding.types';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { useTabs } from 'hooks/useTabs';
import { useContext, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { getRegionsDisplay } from 'shared/utils';
import { useTranslation } from 'react-i18next';
import { getChildLocations } from 'pages/WildfireDetails/WildfireDetails.utils';
import { FloodingStructuredInfo } from 'features/flooding/components/FloodingStructuredInfo';
import { useSelectedIncidentGeoEventId } from 'features/geoEvents/hooks/useSelectedIncidentGeoEventId';
import { useInternalIncidentTab } from 'features/geoEvents/hooks/useInternalIncidentTab';

const TAB_PANEL_ID_PREFIX = 'flooding-details';

const BASE_TABS_TOP = 175;

export const FloodingDetails = (): JSX.Element => {
  const geoEventId = useSelectedIncidentGeoEventId();
  const { geoEvent, isGeoEventLoading, isGeoEventFetchError } =
    useGeoEvent<Flooding>({
      id: geoEventId,
      useCacheBuster: true,
    });
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const { t } = useTranslation();
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const internalTab = useInternalIncidentTab(geoEvent);

  const shelterLocations = getChildLocations(geoEvent);

  const tabData = [
    {
      tabId: 'updates',
      label: t('geoEvent.tabs.updates'),
      enabled: true,
      content: geoEventId && (
        <GeoEventReports geoEventId={geoEventId} geoEventType="flooding" />
      ),
    },
    {
      tabId: 'shelters',
      label: t('geoEvent.tabs.shelters'),
      adornment: <Badge badgeContent={shelterLocations.length} />,
      enabled: shelterLocations.length > 0,
      content: geoEvent && <Locations locations={shelterLocations} />,
    },
    {
      tabId: 'info',
      label: t('geoEvent.tabs.info'),
      enabled: true,
      content: geoEvent && <FloodingStructuredInfo flooding={geoEvent} />,
    },
    internalTab,
  ];

  const evacInfoContainerRef = useRef<HTMLDivElement>(null);

  const { onChangeTab } = useMapEntityDetailsTabs({
    baseTop: BASE_TABS_TOP,
    containerRefs: { evacInfo: evacInfoContainerRef },
  });

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('geoEvent.tabsLabel'),
    tabData,
    onChangeTab,
  });

  const handleTop = (): void => {
    scrollElementRef?.current?.scrollTo({ behavior: 'smooth', top: 0 });
  };

  if (isGeoEventLoading || isGeoEventFetchError || !geoEvent || !geoEventId) {
    return <InlineStatus status={isGeoEventLoading ? 'loading' : 'error'} />;
  }

  const {
    name,
    regions,
    address,
    dateCreated,
    dateModified,
    isActive,
    createdByName,
  } = geoEvent;
  const regionsDisplay = getRegionsDisplay(regions);

  const handleShare = (): void => {
    shareGeoEvent({ name, address, dateModified, shareOrCopyUrl, t });
  };

  return (
    <>
      <Helmet>
        <title>{t('home.floodingTitle', { name })}</title>
      </Helmet>

      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header
          title={name}
          subtitle={geoEvent.data.areaImpacted || undefined}
          hideSubtitleWhenSticky
          caption={regionsDisplay}
        />

        <MapEntityDetailsLayout.Body sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <MapEntityDetailsLayout.Meta data-testid="createdBy">
              <GeoEventUpdateTime geoEvent={geoEvent} />

              <GeoEventCreatedByLine
                createdByName={createdByName}
                dateCreated={dateCreated}
                isActive={isActive}
              />
            </MapEntityDetailsLayout.Meta>

            {(Capacitor.isNativePlatform() || canShareOrCopy) && (
              <MapEntityDetailsLayout.Actions>
                {Capacitor.isNativePlatform() && (
                  <GeoEventNotificationsButton geoEvent={geoEvent} />
                )}

                {canShareOrCopy && (
                  <Button
                    fullWidth
                    color="neutral"
                    variant="outlined"
                    onClick={handleShare}
                    size="large"
                    sx={geoEventShareButtonSx}
                  >
                    {t('geoEvent.shareIncident')}
                  </Button>
                )}
              </MapEntityDetailsLayout.Actions>
            )}
          </Stack>

          {hasGeoEventEvacuationInfo(geoEvent) && (
            <Box ref={evacInfoContainerRef} sx={{ pt: 1 }}>
              <GeoEventEvacuationInfo geoEvent={geoEvent} />
            </Box>
          )}
        </MapEntityDetailsLayout.Body>

        <MapEntityDetailsLayout.Tabs
          tabsProps={tabsProps}
          tabProps={tabProps}
          tabPanels={tabPanels}
          topSlot={
            <GeoEventEvacuationsButton
              geoEvent={geoEvent}
              handleTop={handleTop}
            />
          }
          dontUpdateHolderHeightWhenSticky
        />
      </MapEntityDetailsLayout.Root>
    </>
  );
};
